/**
 * Google Analytics 4 集成
 * 提供类型安全的事件跟踪和页面分析
 */

// 扩展 Window 接口以包含 gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * GA4 事件参数接口
 */
export interface GAEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, unknown>;
}

/**
 * 用户属性接口
 */
export interface GAUserProperties {
  user_role?: string;
  user_type?: string;
  preferred_language?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * 检查 Google Analytics 是否已加载且可用
 */
export function isGAAvailable(): boolean {
  return !!(import.meta.env.PROD && import.meta.env.VITE_GA_MEASUREMENT_ID && window.gtag);
}

/**
 * 跟踪页面浏览
 * @param pagePath 页面路径
 * @param pageTitle 页面标题
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!isGAAvailable()) {
    console.log('[GA] Page view (dev):', { pagePath, pageTitle });
    return;
  }

  window.gtag!('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/**
 * 跟踪自定义事件
 * @param event 事件详情
 */
export function trackEvent(event: GAEvent): void {
  if (!isGAAvailable()) {
    console.log('[GA] Event (dev):', event);
    return;
  }

  const { action, category, label, value, custom_parameters } = event;

  window.gtag!('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...custom_parameters,
  });
}

/**
 * 设置用户属性
 * @param properties 用户属性
 */
export function setUserProperties(properties: GAUserProperties): void {
  if (!isGAAvailable()) {
    console.log('[GA] User properties (dev):', properties);
    return;
  }

  window.gtag!('set', { user_properties: properties });
}

/**
 * 设置用户ID（用于跨设备跟踪）
 * @param userId 用户ID
 */
export function setUserId(userId: string): void {
  if (!isGAAvailable()) {
    console.log('[GA] User ID (dev):', userId);
    return;
  }

  window.gtag!('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
    user_id: userId,
  });
}

/**
 * 预定义的事件跟踪函数
 */
export const Analytics = {
  // 页面导航
  pageView: trackPageView,

  // 用户行为事件
  login: (method: string) =>
    trackEvent({
      action: 'login',
      category: 'engagement',
      label: method,
    }),

  logout: () =>
    trackEvent({
      action: 'logout',
      category: 'engagement',
    }),

  signup: (method: string) =>
    trackEvent({
      action: 'sign_up',
      category: 'engagement',
      label: method,
    }),

  // 业务相关事件
  taskCreate: (taskType?: string) =>
    trackEvent({
      action: 'create_task',
      category: 'logistics',
      label: taskType,
    }),

  taskComplete: (taskId: string) =>
    trackEvent({
      action: 'complete_task',
      category: 'logistics',
      custom_parameters: {
        task_id: taskId,
      },
    }),

  orderView: (orderId: string) =>
    trackEvent({
      action: 'view_order',
      category: 'logistics',
      custom_parameters: {
        order_id: orderId,
      },
    }),

  exportData: (dataType: string, format: string) =>
    trackEvent({
      action: 'export_data',
      category: 'productivity',
      label: `${dataType}_${format}`,
      custom_parameters: {
        export_type: dataType,
        export_format: format,
      },
    }),

  // 搜索和过滤
  search: (searchTerm: string, resultCount: number) =>
    trackEvent({
      action: 'search',
      category: 'engagement',
      label: searchTerm,
      custom_parameters: {
        search_term: searchTerm,
        result_count: resultCount,
      },
    }),

  filterApply: (filterType: string, filterValue: string) =>
    trackEvent({
      action: 'apply_filter',
      category: 'engagement',
      custom_parameters: {
        filter_type: filterType,
        filter_value: filterValue,
      },
    }),

  // 错误跟踪
  error: (errorType: string, errorMessage: string, page?: string) =>
    trackEvent({
      action: 'exception',
      category: 'error',
      custom_parameters: {
        error_type: errorType,
        error_message: errorMessage,
        page_path: page || window.location.pathname,
        fatal: false,
      },
    }),

  // 性能跟踪
  timing: (name: string, value: number, category = 'performance') =>
    trackEvent({
      action: 'timing_complete',
      category,
      custom_parameters: {
        name,
        value,
      },
    }),
};

/**
 * React Hook 用于页面跟踪
 * 使用方法：在路由变化时调用
 */
export function usePageTracking() {
  return {
    trackCurrentPage: () => {
      const path = window.location.pathname;
      const title = document.title;
      trackPageView(path, title);
    },
  };
}
